import type {
  LikeGoodsParams,
  LocalGoodsLike,
  UnlikeGoodsInFolderParams,
} from './types';

/**
 * 찜 싱크 — web 구현.
 *
 * 목적은 네이티브와 같다: 사용자가 방금 찜/찜해제한 상품을 들고 있다가, 다른 화면에서
 * 같은 상품을 만났을 때(그 화면의 API 응답이 아직 옛 상태여도) 찜 상태를 맞춰 보여준다.
 *
 * ## 저장소를 인메모리로 두는 이유
 * 네이티브도 영속 저장이 아니라 프로세스 수명 인메모리 맵이다
 * (Android `instantLikeMap`, iOS `LikeGoodsDatabase.webSyncData`).
 * localStorage 로 영속화하면 재진입(새로고침/다음 방문) 시 서버가 이미 다른 상태인데도
 * 옛 값이 되살아나 서버 응답을 덮어써버린다. 이 캐시는 "지금 세션에서 내가 한 액션"을
 * 반영하기 위한 것이라 세션과 함께 사라지는 게 맞다. 새로고침하면 서버 응답이 곧 진실이다.
 *
 * 따라서 수명 = 페이지 세션. SPA 화면 이동에는 유지되고, 새로고침/탭 종료로 폐기된다.
 * (로그아웃은 `bridge.logout` 이 페이지를 리로드하므로 자연히 비워진다.)
 */
type CacheEntry = {
  isLiked: boolean;
  /** 찜한 폴더 — `unlikeGoodsInFolder`(폴더 삭제) 로 무효화할 대상을 찾는 데만 쓴다. */
  folderSno?: number;
};

const cache = new Map<number, CacheEntry>();

export const likeGoods = ({
  goods_sno,
  is_liked,
  folder_sno,
}: LikeGoodsParams) => {
  cache.set(goods_sno, {
    isLiked: is_liked,
    folderSno: is_liked ? folder_sno : undefined,
  });
};

/**
 * 폴더 삭제 시 그 폴더에 담아둔 상품의 캐시를 찜해제로 뒤집는다.
 *
 * 네이티브는 로컬 DB 로 폴더-상품 관계를 알지만 web 은 이 캐시가 아는 것(= 이번 세션에
 * 그 폴더로 찜한 상품)까지만 처리할 수 있다. 그 외 상품은 서버 응답이 진실이라 문제없다.
 * 이 처리를 빼면 삭제된 폴더의 상품이 세션 내내 찜 상태로 남는다.
 */
export const unlikeGoodsInFolder = ({
  folder_sno,
}: UnlikeGoodsInFolderParams) => {
  Array.from(cache.entries())
    .filter(([, entry]) => entry.folderSno === folder_sno)
    .forEach(([goodsSno]) => {
      cache.set(goodsSno, { isLiked: false });
    });
};

export const getLocalGoodsLike = (): Promise<LocalGoodsLike> => {
  // 네이티브 브릿지가 Promise 라 호출부(await)가 그대로 동작하도록 형태를 맞춘다.
  return Promise.resolve(
    Object.fromEntries(
      Array.from(cache.entries(), ([goodsSno, entry]) => [
        String(goodsSno),
        entry.isLiked,
      ]),
    ),
  );
};